const COLORS = ["#65b0b0", "#6274ad", "#b5d894", "#edb7ce", "#dfbf59", "#b5a5cf", "#f18b99"];
const DURATION = 3600;

export function confettiParticles(width, random = Math.random) {
  return Array.from({ length: Math.min(180, Math.max(90, Math.round(width / 6))) }, () => ({
    x: random() * width,
    y: -12 - random() * 130,
    vx: (random() - 0.5) * 110,
    vy: 175 + random() * 135,
    gravity: 65 + random() * 80,
    delay: random() * 600,
    width: 4 + random() * 5,
    height: 7 + random() * 6,
    rotation: random() * Math.PI * 2,
    spin: (random() - 0.5) * 12,
    flutter: 5 + random() * 7,
    color: COLORS[Math.floor(random() * COLORS.length)],
    round: random() < 0.18,
  }));
}

/** A short rain from the viewport's top edge, on the shared visibility-aware clock. */
export class Confetti {
  constructor({ clock, root = document, random = Math.random, reducedMotion = () => matchMedia("(prefers-reduced-motion: reduce)").matches }) {
    this.clock = clock;
    this.root = root;
    this.random = random;
    this.reducedMotion = reducedMotion;
    this.unsubscribe = clock.subscribe((time) => this.draw(time), () => Boolean(this.canvas));
    this.resize = this.resize.bind(this);
    this.root.defaultView.addEventListener("resize", this.resize);
  }
  start() {
    this.stop();
    if (this.reducedMotion() || this.root.hidden) return false;
    this.canvas = this.root.createElement("canvas");
    this.canvas.className = "celebration-canvas";
    this.canvas.setAttribute("aria-hidden", "true");
    this.context = this.canvas.getContext("2d");
    if (!this.context) { this.canvas = null; return false; }
    this.root.body.append(this.canvas);
    this.resize();
    this.particles = confettiParticles(this.width, this.random);
    this.startedAt = this.clock.time;
    this.clock.burst(DURATION + 100);
    return true;
  }
  resize() {
    if (!this.canvas) return;
    const view = this.root.defaultView;
    this.width = view.innerWidth;
    this.height = view.innerHeight;
    const ratio = Math.min(view.devicePixelRatio || 1, 2);
    this.canvas.width = Math.round(this.width * ratio);
    this.canvas.height = Math.round(this.height * ratio);
    this.canvas.style.width = `${this.width}px`;
    this.canvas.style.height = `${this.height}px`;
    this.context.setTransform(ratio, 0, 0, ratio, 0, 0);
  }
  draw(time) {
    if (!this.canvas) return;
    if (this.reducedMotion() || this.root.hidden || time - this.startedAt >= DURATION) {
      this.stop();
      return;
    }
    const elapsed = time - this.startedAt;
    const context = this.context;
    context.clearRect(0, 0, this.width, this.height);
    const alpha = Math.min(1, (DURATION - elapsed) / 700);
    for (const particle of this.particles) {
      const age = (elapsed - particle.delay) / 1000;
      if (age < 0) continue;
      const x = particle.x + particle.vx * age + Math.sin(age * particle.flutter) * 15;
      const y = particle.y + particle.vy * age + particle.gravity * age * age / 2;
      if (y > this.height + 30) continue;
      context.save();
      context.globalAlpha = alpha;
      context.fillStyle = particle.color;
      context.translate(x, y);
      context.rotate(particle.rotation + particle.spin * age);
      context.scale(Math.cos(age * particle.flutter + particle.rotation), 1);
      if (particle.round) {
        context.beginPath();
        context.arc(0, 0, particle.width / 2, 0, Math.PI * 2);
        context.fill();
      } else context.fillRect(-particle.width / 2, -particle.height / 2, particle.width, particle.height);
      context.restore();
    }
  }
  inFrontOf(dialog) {
    if (this.canvas && dialog.open) dialog.append(this.canvas);
  }
  stop() {
    this.canvas?.remove();
    this.canvas = null;
    this.context = null;
    this.particles = [];
  }
  refreshMotion() {
    if (this.reducedMotion()) this.stop();
  }
  destroy() {
    this.stop();
    this.unsubscribe();
    this.root.defaultView.removeEventListener("resize", this.resize);
  }
}
